<template>
  <label
    v-if="isNativeControl"
    :class="[nativeRootClasses, inheritedClass]"
    :style="inheritedStyle"
    :data-testid="sectionTag"
  >
    <input
      v-bind="passthroughAttrs"
      class="w-radio-btn__native-input"
      type="radio"
      :checked="isChecked"
      :disabled="isDisabled"
      :name="name"
      :value="value"
      @change="handleNativeChange"
    />
    <span class="w-radio-btn__indicator" aria-hidden="true" />
    <span v-if="label || slots.default" class="w-radio-btn__label label-regular-medium">
      <slot>{{ label }}</slot>
    </span>
  </label>

  <v-radio
    v-else-if="hasOwnModelBinding"
    :model-value="model"
    :class="[vuetifyRootClasses, inheritedClass]"
    :style="inheritedStyle"
    :data-testid="sectionTag"
    :label="label"
    :name="name"
    :value="value"
    color="primary"
    hide-details
    density="default"
    :ripple="false"
    v-bind="vuetifyPassthroughAttrs"
    @change="handleVuetifyStandaloneChange"
  >
    <!-- Forward slots (e.g. #label) to the underlying v-radio. Without this, a consumer's
         <template #label> is silently dropped and the radio renders with no label. -->
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData ?? {}" />
    </template>
  </v-radio>

  <v-radio
    v-else
    :class="[vuetifyRootClasses, inheritedClass]"
    :style="inheritedStyle"
    :data-testid="sectionTag"
    :label="label"
    :name="name"
    :value="value"
    color="primary"
    hide-details
    density="default"
    :ripple="false"
    v-bind="vuetifyPassthroughAttrs"
  >
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData ?? {}" />
    </template>
  </v-radio>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, useAttrs, useSlots } from 'vue'
import { tokens } from '@/plugins/vuetify'
import type {
  WSelectionControlModelValue,
  WSelectionControlValue,
} from './WSelectionControl.types'

defineOptions({
  inheritAttrs: false,
})

type WRadioBtnDisabledProp = boolean | ''

export interface WRadioBtnProps {
  sectionTag?: string
  variant?: 'default' | 'compact' | string
  label?: string
  disabled?: WRadioBtnDisabledProp
  name?: string
  value?: WSelectionControlValue
}

const props = withDefaults(defineProps<WRadioBtnProps>(), {
  sectionTag: 'unspecified',
  variant: 'default',
  label: undefined,
  disabled: undefined as WRadioBtnDisabledProp | undefined,
  name: undefined,
  value: undefined,
})

const model = defineModel<WSelectionControlModelValue>()
const attrs = useAttrs()
const slots = useSlots()
const instance = getCurrentInstance()

const isNativeControl = computed(() => props.variant === 'compact')
const isDisabled = computed(() => props.disabled === true || props.disabled === '')
const hasOwnModelBinding = computed(() => {
  const vnodeProps = instance?.vnode.props ?? {}

  return 'modelValue' in vnodeProps
    || 'onUpdate:modelValue' in vnodeProps
    || 'onUpdate:model-value' in vnodeProps
})
const hasOwnDisabledProp = computed(() => {
  return props.disabled !== undefined
})

const inheritedClass = computed(() => attrs.class)
const inheritedStyle = computed(() => attrs.style)
const passthroughAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})
const vuetifyControlProps = computed(() => (
  hasOwnDisabledProp.value ? { disabled: isDisabled.value } : {}
))
const vuetifyPassthroughAttrs = computed(() => {
  const { disabled: _disabled, ...rest } = passthroughAttrs.value

  return {
    ...rest,
    ...vuetifyControlProps.value,
  }
})

const isChecked = computed(() => (
  props.value !== undefined
    ? model.value === props.value
    : Boolean(model.value)
))

const vuetifyRootClasses = computed(() => ({
  'w-radio-btn--default': props.variant === 'default',
}))

const nativeRootClasses = computed(() => ({
  'w-radio-btn': true,
  'w-radio-btn--native': true,
  'w-radio-btn--compact': isNativeControl.value,
  'w-radio-btn--checked': isChecked.value,
  'w-radio-btn--disabled': isDisabled.value,
}))

function handleNativeChange(event: Event) {
  if (isDisabled.value) {
    return
  }

  const target = event.target as HTMLInputElement

  if (target.checked) {
    model.value = props.value ?? true
  }
}

function handleVuetifyStandaloneChange(event: Event) {
  if (isDisabled.value) {
    return
  }

  const target = event.target as HTMLInputElement

  if (target.checked) {
    model.value = props.value ?? true
  }
}
</script>

<style scoped lang="scss">
.w-radio-btn--native {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: fit-content;
  color: v-bind('tokens.color.content.text.primary');
  cursor: pointer;
}

.w-radio-btn--compact {
  min-width: 16px;
  min-height: 16px;
}

.w-radio-btn--disabled {
  cursor: default;
}

.w-radio-btn__native-input {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 16px;
  height: 16px;
  margin: 0;
  opacity: 0;
  cursor: inherit;
}

.w-radio-btn__indicator {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  border: 2px solid v-bind('tokens.color.control.border.default');
  border-radius: v-bind('tokens.borderRadius.control.full');
  background: transparent;
}

.w-radio-btn--checked .w-radio-btn__indicator {
  border-color: v-bind('tokens.color.control.border.selected');
}

.w-radio-btn--checked .w-radio-btn__indicator::after {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: v-bind('tokens.borderRadius.control.full');
  background: v-bind('tokens.color.control.border.selected');
  content: '';
}

.w-radio-btn--disabled .w-radio-btn__indicator {
  border-color: v-bind('tokens.color.control.border.disabled');
}

.w-radio-btn--disabled.w-radio-btn--checked .w-radio-btn__indicator::after {
  background: v-bind('tokens.color.control.border.disabled');
}

.w-radio-btn--native .w-radio-btn__native-input:focus-visible + .w-radio-btn__indicator {
  outline: 2px solid v-bind('tokens.color.focus.ring');
  outline-offset: 2px;
}

.w-radio-btn__label {
  color: v-bind('tokens.color.content.text.primary');
}

.w-radio-btn--disabled .w-radio-btn__label {
  color: v-bind('tokens.color.content.text.muted');
}

.v-selection-control :deep(.v-label) {
  margin-left: 5px;
  color: v-bind('tokens.color.content.text.primary');
  opacity: 1;
}

:deep(.v-selection-control__input > .v-icon) {
  opacity: 1;
}

:deep(
    .v-radio:not(.v-selection-control--dirty)
      > .v-selection-control__wrapper
      > .v-selection-control__input
      > .v-icon
  ) {
  color: v-bind('tokens.color.control.border.default');
}

:deep(.v-selection-control__input::before) {
  border-radius: v-bind('tokens.borderRadius.control.full');
}

:deep(
    .v-radio:not(.v-selection-control--dirty):hover
      > .v-selection-control__wrapper
      > .v-selection-control__input::before
  ) {
  background-color: v-bind('tokens.color.control.background.hover');
  opacity: 1;
}

:deep(
    .v-radio.v-selection-control--dirty:hover
      > .v-selection-control__wrapper
      > .v-selection-control__input::before
  ) {
  background-color: v-bind('tokens.color.control.background.selected');
  opacity: 1;
}

.v-selection-control--disabled {
  opacity: 1 !important;
}

:deep(
    .v-radio.v-selection-control--disabled:not(.v-selection-control--dirty)
      > .v-selection-control__wrapper
      > .v-selection-control__input
      > .v-icon
  ) {
  color: v-bind('tokens.color.control.border.disabled');
}

.v-selection-control--dirty.v-selection-control--disabled {
  opacity: 1 !important;
  color: v-bind('tokens.color.control.border.disabled') !important;
}
</style>
